import type { Component } from 'vue'
import { FileText, Code, FlaskConical, Highlighter, RefreshCw, BarChart3, Search, Palette, Database, Dna, Users, Network } from 'lucide-vue-next'

import { SvgIcons } from '@/components/ui/icon'

export interface Tool {
  id: string
  name: string
  description: string
  route?: string
  url?: string
  icon: Component
  status: 'active' | 'in-dev' | 'planned' | 'disabled'
  badge: 'New' | 'Beta' | 'Alpha' | 'Deprecated' | 'Planned' | 'In Dev' | 'External' | null
  category: 'conversion' | 'Visualisation' | 'analysis' | 'data'
  isExternal?: boolean
}

export const tools: Tool[] = [
  {
    id: 'text-to-json',
    name: 'Text to JSON',
    description: 'Convert rich text and HTML content to JSON format with markdown support',
    route: '/text-to-json',
    icon: FileText,
    status: 'active',
    badge: null,
    category: 'conversion'
  },
  {
    id: 'diseases-directory',
    name: 'Diseases Directory',
    description: 'Manage a directory of diseases and metadata for use in reports',
    route: '/diseases',
    icon: SvgIcons.Stethoscope,
    status: 'active',
    badge: null,
    category: 'data'
  },
  {
    id: 'genes-directory',
    name: 'Genes Directory',
    description: 'Manage a directory of genes and metadata for use in reports',
    route: '/genes',
    icon: SvgIcons.Dna_file,
    status: 'active',
    badge: null,
    category: 'data'
  },
  {
    id: 'patients',
    name: 'Patients',
    description: 'Central patient management with comprehensive data views across all tools',
    route: '/patients',
    icon: Users,
    status: 'active',
    badge: 'New',
    category: 'data'
  },
  {
    id: 'prs-chart-builder',
    name: 'PRS Chart Builder',
    description: 'Create and customise charts for PRS (Polygenic Risk Score) data',
    route: '/prs-chart-builder',
    icon: SvgIcons.Chart01,
    status: 'active',  
    badge: null,
    category: 'Visualisation'
  },
  {
    id: 'report-themer',
    name: 'Report Themer',
    description: 'Preview and customise report designs with interactive colour selection',
    route: '/report-themer',
    icon: Palette,
    status: 'active',
    badge: 'Beta',
    category: 'Visualisation'
  },
  {
    id: 'gccm-editor',
    name: 'GCCM Editor',
    description: 'Inspect and arrange patient GCCM diagrams from 23S Reports Portal data',
    route: '/gccm',
    icon: SvgIcons.Nodes_data,
    status: 'active',
    badge: 'New',
    category: 'Visualisation'
  },
  {
    id: 'journey-visualiser',
    name: 'Journey Builder',
    description: 'Create, edit, and view flow-chart journey diagrams from shared SQLite data',
    route: '/journeys',
    icon: SvgIcons.Node,
    status: 'active',
    badge: 'Alpha',
    category: 'Visualisation'
  },
  {
    id: 'markr-image-annotation',
    name: 'Markr',
    description: 'Add annotations, highlights, and markup to images for reports',
    route: '/markr',
    icon: Highlighter,
    status: 'active',
    badge: "Alpha",
    category: 'analysis'
  },
  {
    id: 'ai-retrieval-lab',
    name: 'AI Retrieval Lab',
    description: 'Index report context and measure report-scoped evidence retrieval',
    route: '/ai-retrieval-lab',
    icon: FlaskConical,
    status: 'active',
    badge: 'Alpha',
    category: 'analysis'
  },
    {
    id: 'timeline-builder',
    name: 'Timeline Builder',
    description: 'Create and customise timelines for reports',
    route: '/timeline-builder',
    icon: SvgIcons.Timelines,
    status: 'active',
    badge: "Alpha",
    category: 'Visualisation'
  },
  {
    id: 'inheritance-patterns',
    name: 'Inheritance Patterns',
    description: 'Build inheritance pattern diagrams for Mendelian genetics',
    route: '/inheritance-patterns',
    icon: SvgIcons.Family_tree,
    status: 'active',
    badge: 'Alpha',
    category: 'Visualisation'
  },
  {
    id: 'peditor',
    name: 'Pedigree Editor',
    description: 'Create and edit pedigree diagrams for genetic reports',
    url: 'https://reports.23strands.com/tools/peditor/',
    icon: SvgIcons.Peditor,
    status: 'active',
    badge: 'External',
    category: 'Visualisation',
    isExternal: true
  }
]

export const getToolById = (id: string): Tool | undefined => {
  return tools.find(tool => tool.id === id)
}

export const getToolByRoute = (route: string): Tool | undefined => {
  return tools.find(tool => tool.route === route)
}

export const isToolRouteActive = (currentPath: string, toolRoute?: string): boolean => {
  return !!toolRoute && (currentPath === toolRoute || currentPath.startsWith(`${toolRoute}/`))
}

export const getActiveTools = (): Tool[] => {
  return tools.filter(tool => tool.status === 'active')
}

export const getComingSoonTools = (): Tool[] => {
  return tools.filter(tool => tool.status === 'in-dev')
}

export const getToolsByCategory = (category: Tool['category']): Tool[] => {
  return tools.filter(tool => tool.category === category)
}

export const getAllCategories = (): Tool['category'][] => {
  return [...new Set(tools.map(tool => tool.category))]
}

export const getCategoryDisplayName = (category: Tool['category']): string => {
  const names = {
    conversion: 'Data Conversion',
    Visualisation: 'Visualisation',
    analysis: 'Analysis',
    data: 'Data Management'
  }
  return names[category] || category
}

export const getCategoryIcon = (category: Tool['category']): Component => {
  const icons = {
    conversion: RefreshCw,
    Visualisation: BarChart3,
    analysis: Search,
    data: Database
  }
  return icons[category] || RefreshCw
}

export const getToolsGroupedByCategory = () => {
  const categories = getAllCategories()
  return categories.map(category => ({
    category,
    displayName: getCategoryDisplayName(category),
    icon: getCategoryIcon(category),
    tools: getToolsByCategory(category)
  }))
}
